import { ZERO_MARK_HEIGHT_PX } from './scale';
import { BAR_OPACITY, type TimelineScrubberSurface } from './surface';
import type { TimelineScrubberBucket } from './types';

export interface BucketProps {
  bucket: TimelineScrubberBucket;
  /** `[0, 1]`, already computed via `normalizeHeight`. */
  heightRatio: number;
  /** Currently previewed via hover/focus — independent of `isSelected`. */
  isDisplayed: boolean;
  isSelected: boolean;
  surface: TimelineScrubberSurface;
  onHoverStart: () => void;
  onSelect: () => void;
}

/**
 * One hour's always-clickable hit area, with its visual bar (or the
 * zero-mark) pinned to the bottom `h-10` band inside it. The button fills
 * whatever height `TimelineScrubber`'s hit-area row gives it — which covers
 * the whole bar band regardless of this bucket's own bar height, so a
 * zero-result hour is exactly as easy to hover/click as the tallest one.
 * See that row's hover/blur handling for why this component only needs
 * `onMouseEnter`/`onFocus`, not leave/blur.
 */
export function Bucket({
  bucket,
  heightRatio,
  isDisplayed,
  isSelected,
  surface,
  onHoverStart,
  onSelect,
}: BucketProps) {
  const opacity = BAR_OPACITY[surface];
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className="relative h-full flex-1 cursor-pointer outline-none"
      aria-label={`T+${bucket.offsetHours}:00, ${bucket.count} external OI${bucket.count === 1 ? '' : 's'}`}
      onMouseEnter={onHoverStart}
      onFocus={onHoverStart}
      onClick={onSelect}
    >
      {/* Visual band: same 40px (h-10) height/position regardless of the
          hit area above. The bar is centered within it (not bottom-anchored)
          so its top/bottom extend equally around the band's fixed vertical
          midpoint — the timeline's horizontal axis — regardless of height.
          A 0 renders as a fixed 2px, pale line — same hue as real
          data, just short and faint enough to read as "exists, but empty"
          rather than "a little bit of data". */}
      <div className="absolute inset-x-0 bottom-0 h-10">
        {/* Hovered and selected both render at full strength. They used to
            be graded apart, but the tooltip no longer trails the selection
            (see `TimelineScrubber`), so the fill is now the only thing
            marking which hour is filtered — a half-opacity bar wasn't
            distinguishable enough from its unselected neighbours to carry
            that on its own. */}
        <span
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-sm bg-(--scrubber-accent) [box-shadow:var(--scrubber-bar-edge)] transition-all"
          style={{
            height: heightRatio > 0 ? `${heightRatio * 100}%` : `${ZERO_MARK_HEIGHT_PX}px`,
            opacity:
              isDisplayed || isSelected
                ? 1
                : heightRatio > 0
                  ? Math.max(opacity.nonzeroMin, heightRatio)
                  : opacity.zero,
          }}
        />
      </div>
    </button>
  );
}
